/* eslint-disable @typescript-eslint/no-non-null-assertion */
/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Sep 05 2023
 **/

import { useCallback, useEffect, useMemo } from "react";
import { useAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { timer } from "rxjs";

import { SidebarID } from "@/frameworks/core/commons";
import { SidebarOption } from "@/frameworks/designStuff/components";
import { JotaiStore } from "@/frameworks/libraries/commons";
import { sidebarReducerAtom } from "./useSidebarState";
import { ActionType, AnimationStatus, Depth, SidebarHandler, SidebarState } from "./types";

export default function useSidebarActions(): SidebarHandler {
	const [state, dispatch] = useAtom(sidebarReducerAtom);

	const isInit = useMemo(() => Object.keys(state.origin).length !== 0, [state.origin]);

	const install = (
		serviceId: string,
		data: {
			id: Depth;
			content: JSX.Element;
			isOpen: boolean;
			option: SidebarOption;
			_animationStatus?: AnimationStatus;
			onBeforeShow?: () => Promise<void>;
			onBeforeHide?: () => Promise<void>;
		}[]
	) => {
		data.forEach(({ id, content, isOpen, option, onBeforeHide, onBeforeShow }) => {
			push({ serviceId, id, content, isOpen, option, onBeforeHide, onBeforeShow });
		});
	};

	const dispose = (serviceId?: string) => {
		if (serviceId) {
			dispatch({ type: "reset", payload: {} });
		} else {
			dispatch({ type: "resetServiceId", payload: { serviceId } });
		}
	};

	const push = ({
		serviceId,
		id,
		content,
		isOpen,
		option,
		onBeforeHide,
		onBeforeShow
	}: {
		serviceId: string;
		id: Depth;
		content: JSX.Element;
		isOpen: boolean;
		option: SidebarOption;
		onBeforeShow?: () => Promise<void>;
		onBeforeHide?: (cause?: string) => Promise<void>;
	}) => {
		dispatch({
			type: "push",
			payload: { serviceId, id, content, isOpen, option, _animationStatus: "init", onBeforeHide, onBeforeShow }
		});
	};

	const show = (id: Depth) => {
		return new Promise((resolve, reject) => {
			try {
				const origin = state.origin[id];
				(async () => {
					if (origin?.onBeforeShow) {
						await origin.onBeforeShow();
					}
					dispatch({ type: "show", payload: { id } });
					resolve(null);
				})();
			} catch (err) {
				reject(err);
			}
		}) as Promise<null>;
	};

	const hide = (id: Depth, cause?: string) => {
		return new Promise((resolve, reject) => {
			try {
				const origin = state.origin[id];
				(async () => {
					if (origin?.onBeforeHide) {
						await origin.onBeforeHide(cause);
					}
					dispatch({ type: "hide", payload: { id } });
					resolve(null);
				})();
			} catch (err) {
				reject(err);
			}
		}) as Promise<null>;
	};

	const setActive = (id: Depth, value: boolean) => {
		value ? show(id) : hide(id);
	};

	const setFocus = (id: Depth | undefined) => {
		dispatch({ type: "setFocus", payload: { id } });
	};

	const hideWithDelay = (id: SidebarID, delay: number, cause?: string) => {
		return new Promise((resolve, reject) => {
			try {
				timer(delay).subscribe(async () => {
					await hide(id, cause);
					resolve(id);
				});
			} catch (err) {
				reject(err);
			}
		});
	};

	const hideAll = useAtomCallback(
		(get, _, type: "all" | "left" | "right" = "all", cause?: string) => {
			return new Promise((resolve, reject) => {
				try {
					const state = get(sidebarReducerAtom);
					const willHideQueue: { id: SidebarID; idx: number }[] = [];

					[...Object.keys(state.order)].forEach((serviceId) => {
						const order = state.order as Record<string, { left: SidebarID[]; right: SidebarID[] }>;

						if (type === "all" || type === "left") {
							[...(order[serviceId]?.left || [])]
								.reverse()
								.filter((id) => state.origin[id].isOpen)
								.forEach((id: SidebarID, idx: number) => {
									willHideQueue.push({ id, idx });
								});
						}

						if (type === "all" || type === "right") {
							[...(order[serviceId]?.right || [])]
								.reverse()
								.filter((id) => state.origin[id].isOpen)
								.forEach((id: SidebarID, idx: number) => {
									willHideQueue.push({ id, idx });
								});
						}
					});
					const recursiveHidingProcess = async () => {
						if (willHideQueue.length === 0) {
							return resolve({ isSuccess: true });
						}
						const { id, idx } = willHideQueue[0];
						willHideQueue.shift();
						await hideWithDelay(id, idx * 500, cause);
						await recursiveHidingProcess();
					};

					recursiveHidingProcess();
				} catch (err) {
					reject(err);
				}
			}) as Promise<{ isSuccess: boolean }>;
		},
		{ store: JotaiStore }
	);

	const get = useAtomCallback(
		useCallback((get, _, id: Depth) => {
			return get(sidebarReducerAtom).origin[id];
		}, []),
		{ store: JotaiStore }
	);

	const getFocus = useAtomCallback(
		useCallback((get) => {
			return get(sidebarReducerAtom).focusedId;
		}, []),
		{ store: JotaiStore }
	);

	const getAll = useAtomCallback(
		useCallback((get) => {
			return get(sidebarReducerAtom).origin;
		}, []),
		{ store: JotaiStore }
	);

	const isOpen = (id: Depth) => {
		return Boolean(get(id)?.isOpen);
	};

	const someIsOpen = (ids: Depth[]) => {
		return ids.some((id) => isOpen(id));
	};

	const updateOption = (id: Depth, option: SidebarOption) => {
		dispatch({ type: "updateOption", payload: { id, option } });
	};

	const updateAnimation = (id: Depth, animationStatus: AnimationStatus) => {
		dispatch({ type: "updateAnimationStatus", payload: { id, _animationStatus: animationStatus } });
	};

	const animationStart: React.AnimationEventHandler<HTMLDivElement> = (evt) => {
		if (evt.target !== evt.currentTarget) return;
		const id = evt.currentTarget.parentElement?.dataset.id as SidebarID;
		if (!id) return;
		// 첫 렌더링시에도 발생함으로, 이를 방지하기 위해 init일 경우 return
		if (state.origin[id]?._animationStatus === "init") return;

		updateAnimation(id, "start");
	};

	const animationEnd: React.AnimationEventHandler = (evt) => {
		if (evt.target !== evt.currentTarget) return;
		const id = evt.currentTarget.parentElement?.dataset.id as SidebarID;
		if (!id) return;
		if (state.origin[id]?._animationStatus === "init") {
			updateAnimation(id, "idle");
		} else {
			updateAnimation(id, "end");
		}
	};

	//! React not support onTransitionStart
	const transitionStart: React.TransitionEventHandler = (evt) => {
		if (evt.target !== evt.currentTarget) return;
		const id = evt.currentTarget.parentElement?.dataset.id as SidebarID;
		if (!id) return;
		// 첫 렌더링시에도 발생함으로, 이를 방지하기 위해 init일 경우 return
		if (state.origin[id]?._animationStatus === "init") return;

		updateAnimation(id, "start");
	};

	const transitionEnd: React.TransitionEventHandler = (evt) => {
		if (evt.target !== evt.currentTarget) return;
		const id = evt.currentTarget.parentElement?.dataset.id as SidebarID;
		if (!id) return;
		if (state.origin[id]?._animationStatus === "init") {
			updateAnimation(id, "idle");
		} else {
			updateAnimation(id, "end");
		}
	};

	/**
	 * 최적화 이슈로 인해 "updateAnimationStatus"와 "push" 액션은 감지를 하지 않습니다.
	 *
	 * @param id - 상태를 감지할 사이드바 아이디 (ALL의 경우 어떤 사이드바가 업데이트 되던간에 감지를 합니다.)
	 * @param callback - 사이드바의 상태를 감지한 후 수행할 콜백 함수 (인자는 상태가 변한 사이드바 아이디의 상태입니다.)
	 * @param deps 의존성 배열
	 */
	const useSidebarEffect = (
		id: Depth | "ALL",
		callback: (state: SidebarState & { action: ActionType }) => void,
		deps?: React.DependencyList
	) => {
		const sidebarOption = useMemo(() => {
			if (state.action === "updateAnimationStatus" || state.action === "push") return null;
			if (id === "ALL") {
				if (!state.id) return null;
				return { ...state.origin[state.id], action: state.action };
			}
			return { ...state.origin[id], action: state.action };
		}, [id, isInit, id !== "ALL" ? state.origin[id] : state.origin]);

		const dependencies = deps ? [...deps, sidebarOption] : [sidebarOption];

		useEffect(() => {
			if (!isInit || state.action === "updateAnimationStatus" || state.action === "push") return;
			sidebarOption && callback(sidebarOption);
		}, [...dependencies, isInit]);
	};

	/**
	 * 사이드바가 숨겨지기 전에 이벤트를 추가합니다.
	 */
	const addOnBeforeHideEventListner = (id: Depth, callback: (cause?: string) => Promise<void>) => {
		dispatch({ type: "addOnBeforeHideEvent", payload: { id, onBeforeHide: callback } });
	};

	const showOverlay = (id: Depth) => {
		const option = state.origin[id].option!;
		dispatch({
			type: "updateOption",
			payload: {
				id,
				option: {
					width: option.width,
					direction: option.direction,
					startX: option.startX,
					overlay: true
				}
			}
		});
	};

	const hideOverlay = (id: Depth) => {
		const option = state.origin[id].option!;
		dispatch({
			type: "updateOption",
			payload: {
				id,
				option: {
					width: option.width,
					direction: option.direction,
					startX: option.startX,
					overlay: false
				}
			}
		});
	};

	return {
		install,
		dispose,
		push,
		show,
		hide,
		hideAll,
		setActive,
		setFocus,
		updateOption,
		get,
		getAll,
		getFocus,
		isOpen,
		someIsOpen,
		useSidebarEffect,
		animationStart,
		animationEnd,
		transitionStart,
		transitionEnd,
		addOnBeforeHideEventListner,
		showOverlay,
		hideOverlay
	};
}
